import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../utils/api';
import { isFirestoreListingId } from '../../utils/properties';
import { uploadPropertyDocument } from '../../utils/propertyDocuments';

const statusLabel = (status) => {
  if (status === 'approved') return 'Approved';
  if (status === 'pending') return 'Pending review';
  if (status === 'rejected') return 'Rejected';
  return status || '—';
};

const PropertyDocumentsPanel = ({ propertyId, isLoggedIn, addToast, onSignIn }) => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [accessStatus, setAccessStatus] = useState('guest');
  const [canUpload, setCanUpload] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [title, setTitle] = useState('');
  const fileRef = useRef(null);
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  const isLive = isFirestoreListingId(propertyId);

  const loadDocuments = useCallback(async () => {
    if (!isLive) {
      setLoading(false);
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.listPropertyDocuments(propertyId);
      const data = res.data || {};
      setDocuments(Array.isArray(data.documents) ? data.documents : []);
      setAccessStatus(data.accessStatus || 'guest');
      setCanUpload(!!data.canUpload);
      setCanReview(!!data.canReview);
    } catch (err) {
      addToastRef.current?.(err.message || 'Could not load documents.', 'error');
    } finally {
      setLoading(false);
    }
  }, [propertyId, isLive]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      addToast?.('Choose a file to upload.', 'error');
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadPropertyDocument(propertyId, file, title || file.name);
      const isAdminUpload = uploaded?.status === 'approved' || accessStatus === 'manager';
      addToast?.(
        isAdminUpload
          ? 'Document uploaded and approved.'
          : 'Document uploaded. Admin approval is required before others can download it.',
        'success'
      );
      setTitle('');
      if (fileRef.current) fileRef.current.value = '';
      await loadDocuments();
    } catch (err) {
      addToast?.(err.message || 'Upload failed.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? You can upload a replacement afterward.`)) return;
    setDeletingId(doc.id);
    try {
      await apiClient.deletePropertyDocument(propertyId, doc.id);
      addToast?.('Document deleted.', 'success');
      await loadDocuments();
    } catch (err) {
      addToast?.(err.message || 'Could not delete document.', 'error');
    } finally {
      setDeletingId('');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await apiClient.downloadPropertyDocument(propertyId, doc.id);
      const url = res.data?.url;
      if (!url) throw new Error('Download link unavailable');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      addToast?.(err.message || 'Download failed.', 'error');
    }
  };

  const handleRequestAccess = async () => {
    if (!isLoggedIn) {
      onSignIn?.();
      return;
    }
    setRequesting(true);
    try {
      const res = await apiClient.requestPropertyDocumentAccess(propertyId, requestMessage);
      const status = res.data?.status || 'pending';
      setAccessStatus(status);
      addToast?.(
        status === 'approved'
          ? 'You already have access to download documents.'
          : 'Access request sent. An admin will review it.',
        'success'
      );
    } catch (err) {
      addToast?.(err.message || 'Could not submit access request.', 'error');
    } finally {
      setRequesting(false);
    }
  };

  const handleReview = async (docId, status) => {
    try {
      await apiClient.reviewPropertyDocument(propertyId, docId, status);
      addToast?.(status === 'approved' ? 'Document approved.' : 'Document rejected.', 'success');
      await loadDocuments();
    } catch (err) {
      addToast?.(err.message || 'Review failed.', 'error');
    }
  };

  if (!isLive) {
    return (
      <div className="detail-card">
        <h3>Due diligence documents</h3>
        <p style={{ color: 'var(--text-light)', lineHeight: 1.6 }}>
          Document uploads are available for live CoOwn listings after admin publish.
        </p>
      </div>
    );
  }

  return (
    <div className="detail-card property-documents-panel">
      <h3>Due diligence documents</h3>
      <p className="property-documents-intro">
        Property documents are reviewed by CoOwn before download. Investors must request access from an admin.
      </p>

      {loading ? (
        <p style={{ color: 'var(--text-light)' }}><i className="fas fa-spinner fa-spin"></i> Loading documents…</p>
      ) : (
        <>
          {documents.length === 0 ? (
            <p style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>No documents published yet.</p>
          ) : (
            <ul className="property-documents-list">
              {documents.map((doc) => (
                <li key={doc.id} className="property-documents-row">
                  <span className="property-documents-row__meta">
                    <i className="fas fa-file-alt" aria-hidden="true"></i>
                    <span>
                      <strong>{doc.title}</strong>
                      {doc.status !== 'approved' ? (
                        <span className={`property-documents-status property-documents-status--${doc.status}`}>
                          {statusLabel(doc.status)}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <span className="property-documents-row__actions">
                    {doc.canDownload ? (
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => handleDownload(doc)}>
                        <i className="fas fa-download"></i> Download
                      </button>
                    ) : null}
                    {canReview && doc.status === 'pending' ? (
                      <>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => handleReview(doc.id, 'approved')}>
                          Approve
                        </button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => handleReview(doc.id, 'rejected')}>
                          Reject
                        </button>
                      </>
                    ) : null}
                    {doc.canDelete ? (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        style={{ color: 'var(--danger)', borderColor: 'rgba(220,53,69,0.35)' }}
                        onClick={() => handleDelete(doc)}
                        disabled={deletingId === doc.id}
                      >
                        <i className={`fas fa-${deletingId === doc.id ? 'spinner fa-spin' : 'trash-alt'}`}></i> Delete
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {canUpload ? (
            <form className="property-documents-upload" onSubmit={handleUpload}>
              <h4>Upload document</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Title</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Inspection report" />
                </div>
                <div className="form-group">
                  <label>File (PDF, Word, or image)</label>
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,image/jpeg,image/png,image/webp" />
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={uploading}>
                <i className={`fas fa-${uploading ? 'spinner fa-spin' : 'cloud-upload-alt'}`}></i>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              {accessStatus === 'manager' ? (
                <p className="property-documents-hint">Admin uploads are approved immediately. Lister uploads need admin approval.</p>
              ) : (
                <p className="property-documents-hint">Your upload will be visible after CoOwn admin approval.</p>
              )}
            </form>
          ) : null}

          {!canUpload && accessStatus !== 'manager' && accessStatus !== 'approved' ? (
            <div className="property-documents-access">
              <h4>Request download access</h4>
              {!isLoggedIn ? (
                <p style={{ color: 'var(--text-light)', marginBottom: '0.75rem' }}>
                  Sign in to request access to property documents.
                </p>
              ) : accessStatus === 'pending' ? (
                <p style={{ color: 'var(--warning)', marginBottom: '0.75rem' }}>
                  <i className="fas fa-hourglass-half"></i> Your access request is pending admin review.
                </p>
              ) : accessStatus === 'denied' ? (
                <p style={{ color: 'var(--danger)', marginBottom: '0.75rem' }}>
                  Your previous request was denied. You may submit a new request with more context.
                </p>
              ) : (
                <p style={{ color: 'var(--text-light)', marginBottom: '0.75rem' }}>
                  Approved documents are gated. Request access and an admin will grant download permission.
                </p>
              )}
              <textarea
                rows={2}
                placeholder="Optional note for the admin (e.g. investor diligence on this offering)"
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                disabled={!isLoggedIn || accessStatus === 'pending'}
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={handleRequestAccess}
                disabled={requesting || (!isLoggedIn ? false : accessStatus === 'pending')}
              >
                {!isLoggedIn ? 'Sign in to request access' : requesting ? 'Sending…' : 'Request access'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default PropertyDocumentsPanel;
